import React, {useEffect, useRef, ReactElement, useState} from 'react';
import {cx} from '@emotion/css';

import {DEFAULT_LOCALE, LANGUAGE_LOCALE} from '@fo/shared-types/constants';
import {
  TrustpilotWidgetVariant,
  trustpilotWidgetVariantMapping,
  TrustpilotTheme,
} from './trustpilotWidgetVariantConfig';

import styles from './TrustpilotWidget.styles';

export {TrustpilotWidgetVariant, trustpilotWidgetVariantMapping, TrustpilotTheme};

interface Props {
  className?: string;
  variant?: TrustpilotWidgetVariant;
  theme?: TrustpilotTheme;
}
function TrustpilotWidget({
  className,
  variant = TrustpilotWidgetVariant.MINI,
  theme = TrustpilotTheme.LIGHT,
}: Readonly<Props>): ReactElement | null {
  // If unhandled variant is given, widget will resolve to mini
  const ref = useRef<HTMLDivElement | null>(null);
  const trustpilotHref = 'https://uk.trustpilot.com/review/fundingoptions.com';
  const [isLoaded, setIsLoaded] = useState(false);
  const [templateId, setTemplateId] = useState(trustpilotWidgetVariantMapping[variant].templateId);
  const [heightStyle, setHeightStyle] = useState(trustpilotWidgetVariantMapping[variant].styleHeight);
  useEffect(() => {
    const trustpilotScriptSrc = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    const scriptId = 'trust-pilot';
    // Ensures there is only one trustpilot script on the page (in case there are multiple widgets on the page)
    if (!document.getElementById(scriptId)) {
      const trustPilotEl = document.createElement('script');
      trustPilotEl.id = scriptId;
      trustPilotEl.src = trustpilotScriptSrc;
      trustPilotEl.async = true;
      document.head.appendChild(trustPilotEl);
      setIsLoaded(true);
    } else {
      setIsLoaded(true);
    }

    if (window.Trustpilot && ref.current) {
      // This to load TrustPilot dynamically
      window.Trustpilot.loadFromElement(ref.current);
    }

    return () => {
      const existScript = document.getElementById(scriptId);
      setIsLoaded(false);
      if (existScript) {
        document.head.removeChild(existScript);
      }
    };
  }, [isLoaded]);

  useEffect(() => {
    setIsLoaded(false);
    setTemplateId(trustpilotWidgetVariantMapping[variant].templateId);
    setHeightStyle(trustpilotWidgetVariantMapping[variant].styleHeight);
    // to let trustpilot rerender in the new variant
    setTimeout(() => {
      setIsLoaded(true);
    }, 100);
  }, [variant]);

  if (!isLoaded) {
    return null;
  }
  return (
    <div
      ref={ref}
      className={cx('trustpilot-widget', styles.container, className)}
      data-review-languages={LANGUAGE_LOCALE}
      data-locale={DEFAULT_LOCALE}
      data-template-id={templateId}
      data-businessunit-id="54c8d9930000ff00057d09a3"
      data-style-height={heightStyle}
      data-style-width="100%"
      data-theme={theme}
      data-stars="4,5"
    >
      <a href={trustpilotHref} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}
export default TrustpilotWidget;
